import { type IntlShape } from "react-intl";
import { z } from "zod";

export const reviewReasonSchema = z.enum(["Error", "OutOfTolerance"]);
export const reviewResolutionActionSchema = z.enum(["Approve", "Update", "Keep"]);
export const bulkReviewResolutionActionSchema = z.enum(["Approve", "Keep"]);
export const bulkReviewResolutionOutcomeSchema = z.enum(["Resolved", "Conflict", "NotApprovable", "NotFound"]);

const reviewFieldBaseSchema = z.object({
	key: z.string().min(1),
	label: z.string().min(1),
	isChanged: z.boolean(),
	isOutOfTolerance: z.boolean(),
	isValid: z.boolean(),
});

export const reviewFieldSchema = z.discriminatedUnion("group", [
	reviewFieldBaseSchema.extend({
		group: z.literal("dimension"),
		valueType: z.literal("number"),
		existingValue: z.number().nullable(),
		capturedValue: z.number().nullable(),
	}),
	reviewFieldBaseSchema.extend({
		group: z.literal("weight"),
		valueType: z.literal("number"),
		existingValue: z.number().nullable(),
		capturedValue: z.number().nullable(),
	}),
	reviewFieldBaseSchema.extend({
		group: z.literal("attribute"),
		valueType: z.enum(["number", "string"]),
		existingValue: z.string().nullable(),
		capturedValue: z.string().nullable(),
	}),
]);

export const reviewChangedFieldSchema = z.object({
	key: z.string().min(1),
	label: z.string().min(1),
});

export const reviewSummarySchema = z.object({
	id: z.uuid(),
	identifier: z.string().min(1),
	productName: z.string().nullable(),
	capturedBy: z.string().min(1),
	capturedAt: z.iso.datetime({ offset: true }),
	reason: reviewReasonSchema,
	hasExistingProduct: z.boolean(),
	changedFields: z.array(reviewChangedFieldSchema),
});

export const reviewListSchema = z.object({
	items: z.array(reviewSummarySchema),
	totalCount: z.number().int().nonnegative(),
	pageNumber: z.number().int().nonnegative(),
	pageSize: z.number().int().positive(),
});

export const getProductReviewsRequestSchema = z.object({
	pageNumber: z.number().int().nonnegative(),
	pageSize: z.number().int().positive(),
});

export const getProductReviewsByIdentifiersRequestSchema = z.object({
	identifiers: z.array(z.string().min(1)).min(1),
});

export const reviewExistingProductSchema = z.object({
	productKey: z.uuid(),
	version: z.number().int().nonnegative(),
	identifiers: z.record(z.string(), z.string()),
	name: z.string().nullable(),
	description: z.string().nullable(),
	imageUrl: z.string().nullable(),
});

export const reviewDetailSchema = reviewSummarySchema.extend({
	existingProduct: reviewExistingProductSchema.nullable(),
	canApprove: z.boolean(),
	fields: z.array(reviewFieldSchema),
});

export const productReviewDimensionUpdatesSchema = z.object({
	length: z.number().optional(),
	width: z.number().optional(),
	height: z.number().optional(),
});

export const productReviewUpdateValuesSchema = z.object({
	dimensions: productReviewDimensionUpdatesSchema.optional(),
	weight: z.number().optional(),
	customAttributes: z.record(z.string(), z.string()).optional(),
});

export const resolveProductReviewRequestSchema = z.object({
	action: reviewResolutionActionSchema,
	values: productReviewUpdateValuesSchema.optional(),
});

export const resolveProductReviewResponseSchema = z.object({
	id: z.uuid(),
	action: reviewResolutionActionSchema,
	productKey: z.uuid(),
});

export const bulkResolveProductReviewsRequestSchema = z.object({
	ids: z.array(z.uuid()).min(1),
	action: bulkReviewResolutionActionSchema,
});

export const bulkReviewResolutionResultSchema = z.object({
	id: z.uuid(),
	outcome: bulkReviewResolutionOutcomeSchema,
});

export const bulkResolveProductReviewsResponseSchema = z.object({
	results: z.array(bulkReviewResolutionResultSchema),
});

export const getProductReviewFormValuesSchema = (fields: readonly ReviewField[], intl: IntlShape) => {
	const changedFieldSchemas = Object.fromEntries(
		fields
			.filter((field) => field.isChanged)
			.map((field) => {
				if (field.valueType === "number") {
					const numberSchema =
						field.group === "dimension"
							? z.coerce.number().positive(
									intl.formatMessage({
										id: "Dimension must be greater than zero",
										defaultMessage: "Dimension must be greater than zero",
									}),
								)
							: z.coerce.number();

					return [field.key, numberSchema];
				}

				return [field.key, z.string()];
			}),
	);

	return z.object(changedFieldSchemas).strict();
};

export type ReviewField = z.infer<typeof reviewFieldSchema>;
export type ReviewChangedField = z.infer<typeof reviewChangedFieldSchema>;
export type ReviewSummary = z.infer<typeof reviewSummarySchema>;
export type ReviewList = z.infer<typeof reviewListSchema>;
export type ReviewDetail = z.infer<typeof reviewDetailSchema>;
export type GetProductReviewsRequest = z.infer<typeof getProductReviewsRequestSchema>;
export type GetProductReviewsByIdentifiersRequest = z.infer<typeof getProductReviewsByIdentifiersRequestSchema>;
export type ResolveProductReviewRequest = z.infer<typeof resolveProductReviewRequestSchema>;
export type ResolveProductReviewResponse = z.infer<typeof resolveProductReviewResponseSchema>;
export type BulkResolveProductReviewsRequest = z.infer<typeof bulkResolveProductReviewsRequestSchema>;
export type BulkResolveProductReviewsResponse = z.infer<typeof bulkResolveProductReviewsResponseSchema>;
export type ProductReviewFormValues = z.infer<ReturnType<typeof getProductReviewFormValuesSchema>>;
